// src/config/holder.js
// ---------------------------------------------------------------------------
// Who is holding the code, and the one line the guest reads about them.
//
// This started as a boolean. A role was a person or it was not, and a person got
// "Your bartender today is ...". That was too narrow in both directions: it said
// "bartender" to a shopper in a hypermarket, and it had nothing to say when the
// thing holding the code has a name but is not a human. A code can sit with:
//
//   a person     a promoter, a bartender, a merchandiser
//   a store      a branch, a franchise, a retailer running it themselves
//   an event     a festival, a roadshow, an activation with its own name
//   nobody       a shelf talker, an on-pack code, a kiosk, a delivery listing
//
// The fourth is not an error state. Plenty of scan points genuinely have no one
// behind them, and the right thing to show is nothing.
//
// **Mirrored from sipnscore-admin/src/config/holder.js. Change one, change
// both.** Same arrangement as branding.js and guestFields.js, and for the same
// reason: the guest app renders this and must not own a second definition of it.
// The tests live with the admin copy.
// ---------------------------------------------------------------------------

/**
 * `fields` is what the editor asks for. A person has two, because a first name
 * on its own reads as a nickname on a client's screen; everything else has one,
 * because a store does not have a surname.
 */
export const HOLDER_KINDS = [
  {
    id: "person",
    label: "A person",
    hint: "Someone standing there: a promoter, a bartender, a merchandiser.",
    fields: ["first", "last"],
  },
  {
    id: "store",
    label: "A store",
    hint: "The retailer is running it themselves, with no individual named.",
    fields: ["name"],
    prompt: "Store name",
    promptHint: "For example Checkers Sandton City.",
  },
  {
    id: "event",
    label: "An event",
    hint: "A festival, a roadshow or an activation with its own name.",
    fields: ["name"],
    prompt: "Event name",
    promptHint: "For example Rocking the Daisies.",
  },
  {
    id: "none",
    label: "Nobody",
    hint: "A shelf code, an on-pack code, a kiosk or a listing. The guest is told nothing about who is behind it.",
    fields: [],
  },
];

export const HOLDER_KIND_BY_ID = Object.fromEntries(
  HOLDER_KINDS.map((k) => [k.id, k])
);

/** Two name fields or one, so an editor can ask the right question. */
export function holderFields(kind) {
  return HOLDER_KIND_BY_ID[kind]?.fields || [];
}

/**
 * The sentence the guest reads, or null for no sentence at all.
 *
 * Null is returned for `none`, for an unknown kind, and whenever there is no
 * name, whatever the kind says. A missing name is the common case on an older
 * scan point, and "Your promoter today is" with nothing after it is worse than
 * silence.
 *
 * The role only appears for a person. A store is not a promoter and a festival
 * does not have a job title.
 *
 * @param {{kind?: string, name?: string, roleLabel?: string}} holder
 * @returns {string|null}
 */
export function holderLine({ kind, name, roleLabel } = {}) {
  const who = String(name || "").trim();
  if (!who) return null;

  switch (kind) {
    case "person": {
      const role = String(roleLabel || "").trim().toLowerCase() || "host";
      return `Your ${role} today is ${who}`;
    }
    case "store":
      return `Brought to you by ${who}`;
    case "event":
      return `Live at ${who}`;
    default:
      // Includes "none", and anything written by a future version of the
      // editor that this build does not know about. Silence is the safe answer.
      return null;
  }
}

/** First and last into the single stored name, without a stray space. */
export function holderNameFrom(kind, { first = "", last = "", name = "" } = {}) {
  if (kind === "person") return `${first.trim()} ${last.trim()}`.trim();
  if (kind === "none") return "";
  return String(name || "").trim();
}

/** The stored name split back out, for an editor with two fields. */
export function holderNameParts(kind, name = "") {
  const v = String(name || "").trim();
  if (kind !== "person") return { first: "", last: "", name: v };
  const bits = v.split(/\s+/);
  return { first: bits[0] || "", last: bits.slice(1).join(" "), name: "" };
}

/**
 * What an existing scan point means, before anyone chose a kind.
 *
 * Everything written before this file existed carried a name and a rolePerson
 * boolean. A named person stays a person; anything else becomes `none`, which
 * is the conservative reading: the alternative is inventing a store name from a
 * field that was never one.
 */
export function holderFromLegacy({ bartenderName, rolePerson } = {}) {
  const name = String(bartenderName || "").trim();
  if (name && rolePerson === true) return { kind: "person", name };
  return { kind: "none", name: "" };
}
